import { cleanSourceText, isoDatesToSpanish } from '@tenders/shared';
import { countryConfig } from '../country';
import { daysWord, formatBudget, withCurrency } from './format';

/**
 * «Условия поставки» — the public per-lot supply/commercial detail
 * (`TenderView.lotDetails`). goszakup fills it with ready strings; etender
 * (since 2026-10-09) with numbers — `deliveryTerm` / `paymentTermDays` in DAYS —
 * and, for a multi-position lot, a `positions[]` list. Known keys only, in a
 * fixed reading order; anything else on the blob is ignored.
 */
export const LOT_DETAIL_KEYS = [
  'unitPrice',
  'qty',
  'unit',
  'deliveryPlace',
  'deliveryTerm',
  'advancePct',
  'paymentType',
  'paymentTermDays',
  'incoterms',
  'purchaseType',
  'batchLotCount',
  'batchTotal',
  'legalAddress',
] as const;

/**
 * The single-position keys «Что закупают» (`CatalogItem.positions`) already
 * shows — the tender page drops these rows from «Условия поставки» whenever the
 * payload carries `positions`, so the same price is not printed twice.
 */
export const LOT_POSITION_ROW_KEYS: ReadonlySet<string> = new Set(['unitPrice', 'qty', 'unit']);

const MONEY_KEYS = new Set(['unitPrice', 'batchTotal']);
const COUNT_KEYS = new Set(['qty', 'batchLotCount']);
const DAY_KEYS = new Set(['deliveryTerm', 'paymentTermDays']);

/**
 * goszakup's «Тип закупки» is a two-value enum the source publishes only in
 * Russian (`RefTypeTrade.nameRu`). On a Kazakh page it read «Первая закупка»
 * among Kazakh labels (phone QA, 2026-10-16), so kk gets the two known values
 * translated; anything else stays verbatim, like every other source string.
 */
const PURCHASE_TYPE_KK: Record<string, string> = {
  'Первая закупка': 'Бірінші сатып алу',
  'Повторная закупка': 'Қайталама сатып алу',
};

/** `[key, display value]` rows; the label is `tender.lotDetails.<key>`. */
export function lotDetailRows(
  lotDetail: Record<string, unknown> | null | undefined,
  locale: string,
): Array<[string, string]> {
  if (!lotDetail) return [];
  const numberLocale = locale === 'ru' ? 'ru-RU' : locale;
  const rows: Array<[string, string]> = [];
  for (const k of LOT_DETAIL_KEYS) {
    const v = lotDetail[k];
    if (v == null || (typeof v === 'string' && v.trim().length === 0)) continue;
    if (typeof v === 'number') {
      if (!Number.isFinite(v)) continue;
      if (MONEY_KEYS.has(k)) {
        // Spain: a total under one euro is a placeholder the buyer typed, not an amount
        // («Importe total del expediente 0,04 €», QA 2026-10-21) — no row at all.
        if (countryConfig.currency.numberLocale && v < 1) continue;
        // Euros keep their cents (`currency.numberLocale`); UZ/KZ round as before.
        const raw = countryConfig.currency.numberLocale ? String(v) : String(Math.round(v));
        const amount = formatBudget(raw) ?? String(v);
        // Spain prints the unit beside the figure («111.600 €», Chile «$ 111.600»): a bare
        // «Importe total del expediente 111.600» read as a count (QA 20.10).
        // UZ/KZ (no `numberLocale`) keep the bare amount they always showed.
        rows.push([
          k,
          countryConfig.currency.numberLocale
            ? withCurrency(amount, countryConfig.currency.symbol)
            : amount,
        ]);
      } else if (k === 'advancePct') rows.push([k, `${v}%`]);
      else if (COUNT_KEYS.has(k)) rows.push([k, v.toLocaleString(numberLocale)]);
      else if (DAY_KEYS.has(k) && v > 0) rows.push([k, `${v} ${daysWord(v, locale)}`]);
      continue;
    }
    if (typeof v === 'string') {
      const kk = k === 'purchaseType' && locale === 'kk' ? PURCHASE_TYPE_KK[v.trim()] : undefined;
      // Spain: the PLACSP contract term is stored «Del 2027-01-01 al 2027-12-31» (its ISO
      // dates are read back by the API) and printed as such on 1,174 live lots
      // (2026-10-21); a Spanish page spells the dates. Place names pass through as-is.
      const es = countryConfig.code === 'es' ? isoDatesToSpanish(cleanSourceText(v)) : undefined;
      rows.push([k, kk ?? es ?? v]);
    }
  }
  return rows;
}

/**
 * «Порядок проведения» keys that hold the delivery place: `deliveryAddress`
 * (xarid-prequest `GetLot`) and `address` — which every harvester that fills
 * «Условия поставки» writes from the same delivery-address field (uzex-xarid
 * and etender `delivery_address`, the prequest list `address`, auctionx
 * `street`). tender-mc's `address` is a construction site, but tender-mc has
 * no «Условия поставки», so the rule below never reaches it.
 */
const PROCEDURE_PLACE_KEYS = new Set(['address', 'deliveryAddress']);

/**
 * Should «Порядок проведения» skip this row because «Условия поставки» above
 * already names the delivery place (Igor, 09.10: uzex-xarid and prequest lots
 * showed the address twice)? The supply-terms row wins — it is the structured
 * per-lot field; the procedure row is the older free-text copy. A lot with no
 * `deliveryPlace` in its supply terms keeps the procedure row exactly as before.
 */
export function procedureRowDuplicatesLotPlace(
  procedureKey: string,
  lotRows: ReadonlyArray<readonly [string, string]>,
): boolean {
  return PROCEDURE_PLACE_KEYS.has(procedureKey) && lotRows.some(([k]) => k === 'deliveryPlace');
}

export interface LotPosition {
  name: string | null;
  code: string | null;
  qty: number | null;
  unit: string | null;
  unitPrice: number | null;
  total: number | null;
  deliveryTermDays: number | null;
}

const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;
const strOrNull = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

/** The `positions[]` list of a multi-position lot, defensively read. */
export function lotPositions(lotDetail: Record<string, unknown> | null | undefined): {
  positions: LotPosition[];
  total: number;
} {
  const raw = lotDetail?.positions;
  if (!Array.isArray(raw)) return { positions: [], total: 0 };
  const positions = raw
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .map((p) => ({
      name: strOrNull(p.name),
      code: strOrNull(p.code),
      qty: numOrNull(p.qty),
      unit: strOrNull(p.unit),
      unitPrice: numOrNull(p.unitPrice),
      total: numOrNull(p.total),
      deliveryTermDays: numOrNull(p.deliveryTermDays),
    }));
  const declared = numOrNull(lotDetail?.positionsTotal);
  return { positions, total: Math.max(positions.length, declared ?? 0) };
}

/** «20 шт × 40 000 000 = 800 000 000» — the parts a position actually carries. */
export function positionLine(p: LotPosition, locale: string): string {
  const numberLocale = locale === 'ru' ? 'ru-RU' : locale;
  const qty =
    p.qty != null ? `${p.qty.toLocaleString(numberLocale)}${p.unit ? ` ${p.unit}` : ''}` : '';
  const price = p.unitPrice != null ? formatBudget(String(Math.round(p.unitPrice))) : null;
  const total = p.total != null ? formatBudget(String(Math.round(p.total))) : null;
  let line = qty;
  if (price) line = line ? `${line} × ${price}` : price;
  if (total) line = line ? `${line} = ${total}` : total;
  if (p.deliveryTermDays != null && p.deliveryTermDays > 0) {
    const days = `${p.deliveryTermDays} ${daysWord(p.deliveryTermDays, locale)}`;
    line = line ? `${line} · ${days}` : days;
  }
  return line;
}
